import React, { useMemo } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { useTerritory } from '../contexts/TerritoryContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MapPin } from 'lucide-react';

export const TerritorySelector = ({ onComplete, showAllCommunes = false }) => {
  const { t } = useI18n();
  const {
    countries,
    regions,
    communes,
    selectedCountry,
    selectedRegion,
    selectedCommune,
    selectCountry,
    selectRegion,
    selectCommune,
    loading
  } = useTerritory();

  // Generate unique keys based on available options to force re-render
  const countryKey = useMemo(() => `country-${countries.map(c => c.id).join('-')}`, [countries]);
  const regionKey = useMemo(() => `region-${selectedCountry?.id || 'none'}-${regions.map(r => r.id).join('-')}`, [selectedCountry, regions]);
  const communeKey = useMemo(() => `commune-${selectedRegion?.id || 'none'}-${communes.map(c => c.id).join('-')}`, [selectedRegion, communes]);

  if (loading) {
    return (
      <div className="animate-pulse flex gap-4">
        <div className="h-10 bg-gray-200 rounded-xl w-32"></div>
        <div className="h-10 bg-gray-200 rounded-xl w-48"></div>
        <div className="h-10 bg-gray-200 rounded-xl w-48"></div>
      </div>
    );
  }

  const handleCountryChange = (value) => {
    const country = countries.find(c => c.id === value);
    if (country) {
      selectCountry(country);
    }
  };

  const handleRegionChange = (value) => {
    const region = regions.find(r => r.id === value);
    if (region) {
      selectRegion(region);
    }
  };

  const handleCommuneChange = (value) => {
    if (value === 'all') {
      selectCommune(null);
      onComplete?.(null);
    } else {
      const commune = communes.find(c => c.id === value);
      if (commune) {
        selectCommune(commune);
        onComplete?.(commune);
      }
    }
  };

  // Convert value to string for Radix Select (it requires string values)
  const countryValue = selectedCountry?.id || '';
  const regionValue = selectedRegion?.id || '';
  const communeValue = selectedCommune?.id || 'all';

  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3" data-testid="territory-selector">
      {/* Country */}
      {countries.length > 0 && (
        <Select
          key={countryKey}
          value={countryValue}
          onValueChange={handleCountryChange}
        >
          <SelectTrigger className="w-full sm:w-[160px] rounded-xl bg-white border-gray-200" data-testid="country-select">
            <div className="flex items-center gap-2">
              {selectedCountry?.flag_emoji && <span>{selectedCountry.flag_emoji}</span>}
              <span className="truncate">{selectedCountry?.name || t('selectCountry')}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {countries.map((country) => (
              <SelectItem key={country.id} value={country.id}>
                {country.flag_emoji} {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Region */}
      {regions.length > 0 && (
        <Select
          key={regionKey}
          value={regionValue}
          onValueChange={handleRegionChange}
        >
          <SelectTrigger className="w-full sm:w-[220px] rounded-xl bg-white border-gray-200" data-testid="region-select">
            <span className="truncate">{selectedRegion?.name || t('selectRegion')}</span>
          </SelectTrigger>
          <SelectContent>
            {regions.map((region) => (
              <SelectItem key={region.id} value={region.id}>
                {region.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Commune */}
      {communes.length > 0 && (
        <Select
          key={communeKey}
          value={communeValue}
          onValueChange={handleCommuneChange}
        >
          <SelectTrigger className="col-span-2 w-full sm:w-[220px] rounded-xl bg-white border-gray-200" data-testid="commune-select">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#00CC66] flex-shrink-0" />
              <span className="truncate">
                {selectedCommune?.name || t('allCommunes')}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {showAllCommunes && (
              <SelectItem value="all">{t('allCommunes')}</SelectItem>
            )}
            {communes.map((commune) => (
              <SelectItem key={commune.id} value={commune.id}>
                {commune.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};
